/**
 * #1674 (review of PR #1689): what the daily check of the dbt repository (`dbt_repo`) says, on the
 * Destination screen and in the credential email. The check opens the saved repository with the
 * saved token and reads its refs, so `missing_object` there is the repository address that answers
 * 404 (or a private one the token cannot see) or the saved dbt branch that the repository does not
 * have. Neither is a table or a stage, and neither is fixed by a new token alone, so the load's
 * words (`LOAD_CAUSE_SENTENCES`) and "replace the destination credential" are never used for it.
 *
 * The outcome is unchanged: the row stays `rejected` with cause `missing_object` or `auth`. Only
 * the words change. Shared, so the runner's renderer and the client read one rule.
 */

interface CheckTarget {
  targetType: string;
  causeCode?: string | null;
}

/** Where an instance admin changes the repository, its branch and its token. */
export const DBT_REPO_SETTINGS_PLACE = "Settings, Destination, dbt repository";

/** The daily check's cause for a `dbt_repo` target, in the form of the other tables (no full stop). */
const DBT_REPO_CHECK_SENTENCES: Readonly<Record<string, string>> = {
  missing_object:
    "The dbt repository or its dbt branch was not found, or the access token cannot read the repository",
  auth: "The dbt repository refused the access token",
};

/** The cause sentence for a `dbt_repo` target, or null when its cause has no dbt words. */
export function dbtRepoCheckSentence(target: CheckTarget): string | null {
  if (target.targetType !== "dbt_repo" || !target.causeCode) return null;
  return Object.hasOwn(DBT_REPO_CHECK_SENTENCES, target.causeCode)
    ? (DBT_REPO_CHECK_SENTENCES[target.causeCode] ?? null)
    : null;
}

/**
 * True when the refusal is about what the binding names (the address or the branch), not the
 * token alone: a `dbt_repo` target refused with `missing_object`.
 */
export function isDbtRepoSettingsRefusal(target: CheckTarget): boolean {
  return target.targetType === "dbt_repo" && target.causeCode === "missing_object";
}

/** The badge and headline word in place of "Credentials" for such a refusal. */
export const DBT_REPO_SETTINGS_REFUSED_LABEL = "dbt repository settings refused";

/** The next step for a `dbt_repo` target, by its cause (an email reaches every role). */
export function dbtRepoCheckStep(target: CheckTarget): string {
  if (isDbtRepoSettingsRefusal(target)) {
    return `The next dbt run will fail until this is fixed. Ask an instance admin to check the repository address, the dbt branch and the access token in ${DBT_REPO_SETTINGS_PLACE}.`;
  }
  return `The next dbt run will fail until this is fixed. Ask an instance admin to replace the access token in ${DBT_REPO_SETTINGS_PLACE}.`;
}
